import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Badge } from "@/components/badge";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import { useNavigate } from "react-router-dom";
import SortableTableHeader from "@/components/SortableTableHeader";

type SortField =
  | "user_name"
  | "exam_set_title"
  | "exam_type"
  | "total_score"
  | "created_at"
  | null;
type SortOrder = "asc" | "desc";

interface ExamResultsTableProps {
  examResults: IExamReport[];
  isLoading: boolean;
  error: Error | null;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

const ExamResultsTable: React.FC<ExamResultsTableProps> = ({
  examResults,
  isLoading,
  error,
  sortField,
  sortOrder,
  onSort,
}) => {
  const navigate = useNavigate();

  // Navigate to exam result detail page
  const handleViewResult = (resultId: string) => {
    navigate(`/exam_results/${resultId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScore = (score?: number | null) => {
    if (score === undefined || score === null) return "-";
    return score.toString();
  };

  const renderSubjectScores = (subjects: IExamReport["subjects"]) => {
    if (!subjects || subjects.length === 0) {
      return <span className="text-gray-500">-</span>;
    }

    return (
      <div className="space-y-1">
        {subjects.map((subject) => (
          <div key={subject.subject} className="flex items-center">
            <Badge color="zinc" className="text-xs">
              {subject.subject === 'reading_writing' ? 'R&W' : 
               subject.subject === 'math' ? 'Math' : 
               subject.subject.charAt(0).toUpperCase() + subject.subject.slice(1)}
            </Badge>
            <span className="ml-2 font-medium text-sm">
              {formatScore(subject.total_score)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const getTotalScore = (totalScore?: number) => {
    return formatScore(totalScore);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading exam results...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error occurred: {error.toString()}
      </div>
    );
  }

  if (examResults.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        No exam results found.
      </div>
    );
  }

  return (
    <Table className="mt-4">
      <TableHead>
        <TableRow>
          <SortableTableHeader
            sortKey="user_name"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Student
          </SortableTableHeader>
          <SortableTableHeader
            sortKey="exam_set_title"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Exam
          </SortableTableHeader>
          <TableHeader>Subjects</TableHeader>
          <SortableTableHeader
            sortKey="total_score"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Total Score
          </SortableTableHeader>
          <SortableTableHeader
            sortKey="created_at"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Created At
          </SortableTableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {examResults.map((result) => (
          <TableRow
            key={result.id}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => handleViewResult(result.id)}
          >
            <TableCell>
              <div className="font-medium">{result?.user?.name}</div>
              <div className="text-xs text-zinc-500">{result?.user?.email}</div>
            </TableCell>
            <TableCell>
              <div className="font-medium">
                {result?.exam_set?.title || "-"}
              </div>
              <div className="text-xs text-zinc-500">{result?.exam_type}</div>
            </TableCell>
            <TableCell>
              {renderSubjectScores(result?.subjects)}
            </TableCell>
            <TableCell>
              <div className="font-medium">
                {getTotalScore(result.total_score)}
              </div>
            </TableCell>
            <TableCell className="text-zinc-500">
              {formatDate(result.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ExamResultsTable;
